import React from 'react';
import {Platform, Pressable, StyleSheet, View} from 'react-native';
import Localized from 'src/constants/AppStrings';
import Styles from '../../Styles';
import {ScreenProps} from 'src/types/Screen';
import AccountStore from 'src/stores/AccountStore';
import {GiftItem} from 'src/types/Snack';
import AVText from './../AVText';
import {getDescriber} from './../descriptor/DescriptorType';

import UnReadlIcon from 'src/components/img/svg/UnReadIcon';
import {getNotificationDescriber} from 'src/components/elements/notificationDescriptor/DescriptorType';
import Swipeable from 'react-native-gesture-handler/ReanimatedSwipeable';
import RenderRightDelectActions from './../RenderRightDelectActions';
import {useDeleteSwipe} from '../hooks';
import {useGlobalize} from '../../../i18n/hook/useGlobalize';
import {
  withGlobalize,
  WithGlobalizeProps,
} from '../../../i18n/HOC/withGlobalize';

const SnackCanteenItem = ({
  snack,
  onPress,
  onDelete,
  globalize,
}: ScreenProps &
  WithGlobalizeProps & {
    snack: GiftItem;
    onPress: () => void;
    onDelete: () => void;
  }) => {
  const [
    maxSwipeDistance,
    screenWidth,
    swipeableRef,
    handleSwipeStart,
    handleSwipeEnd,
    handlePress,
  ] = useDeleteSwipe({onPress});

  const {Labels} = Localized;
  const describerprops = {
    snack,
    globalize,
  };

  const {formatCurrency} = useGlobalize();

  const renderDescription = (isSender: boolean) => {
    return isSender
      ? Labels.your_snack_sent
      : Labels.formatString(Labels.sent_you_snack, snack.senderName);
  };

  const renderMessage = (isSender: boolean) => {
    return isSender
      ? Labels.formatString(
          Labels.we_have_sent_your_snack,
          formatCurrency(snack.amount, AccountStore.getCurrency(), {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          }),
          snack.email,
        )
      : getDescriber().snackMessageDescriptor(describerprops);
  };

  const isSender = snack.senderAccountId === AccountStore.getAccountId();

  return (
    <>
      <Swipeable
        onSwipeableWillOpen={handleSwipeStart}
        onSwipeableClose={handleSwipeEnd}
        ref={swipeableRef}
        renderRightActions={(progressAnimatedValue, dragAnimatedValue) => (
          <RenderRightDelectActions
            dragAnimatedValue={dragAnimatedValue}
            onPress={() => {
              swipeableRef.current?.close();
              onDelete();
            }}
          />
        )}
        containerStyle={{
          width: Platform.OS === 'web' ? undefined : screenWidth,
        }}
        rightThreshold={maxSwipeDistance}
        overshootRight={false}
      >
        <Pressable
          onPress={handlePress}
          style={[
            styles.pressableStyle,
            getDescriber().getSnackItemStyleDescriptor().containerBackground,
          ]}
          accessible
          accessibilityLabel="Inbox Snack Item"
          testID="qa_btn_InboxSnackItem"
          nativeID="qa_btn_InboxSnackItem"
        >
          {getDescriber().snackItemGiftBoxTemplateDescriptor(describerprops)}
          <View
            style={styles.mainView}
            accessible
            testID="qa_btn_InboxSnackItem"
          >
            <View style={styles.titleHeaderView}>
              <AVText
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm8}
                style={getDescriber().getSnackItemStyleDescriptor().titleHeader}
                accessible
                testID="qa_NotificationType_txt"
                nativeID="qa_NotificationType_txt"
              >
                {Localized.Buttons.send_a_snack}
              </AVText>
              <AVText
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm8}
                style={getDescriber().getSnackItemStyleDescriptor().date}
                accessible
                testID="qa_MessageDate_txt"
                nativeID="qa_MessageDate_txt"
              >
                {getDescriber().snackItemDateDescriptor(describerprops)}
              </AVText>
            </View>
            <View style={styles.readView}>
              {!snack.isRead && (
                <UnReadlIcon
                  color={getNotificationDescriber().getUnreadMessageIconColor()}
                />
              )}
              <AVText
                style={getDescriber().getSnackItemStyleDescriptor().subject}
                numberOfLines={getDescriber().snackItemNumberDescriptor()}
                accessible
                testID="qa_MessageSubject_txt"
                nativeID="qa_MessageSubject_txt"
              >
                {renderDescription(isSender)}
              </AVText>
            </View>

            <AVText
              style={getDescriber().getSnackItemStyleDescriptor().message}
              numberOfLines={getDescriber().snackItemNumberDescriptor()}
              accessible
              testID="qa_MessageBody_txt"
              nativeID="qa_MessageBody_txt"
            >
              {renderMessage(isSender)}
            </AVText>
          </View>
        </Pressable>
      </Swipeable>
      <View style={styles.lineWrapper}>
        <View style={styles.line}></View>
      </View>
    </>
  );
};
const styles = StyleSheet.create({
  lineWrapper: {
    width: '100%',
    paddingHorizontal: 20,
  },
  line: {
    width: '100%',
    borderBottomWidth: 1,
    borderBottomColor: '#9A9A9F',
  },
  icon: {
    alignItems: 'center',
    borderRadius: 32,
    backgroundColor: Styles.darkBlueRevolveColor,
    width: 50,
    justifyContent: 'center',
    height: 50,
  },
  message: {
    flex: 1,
    color: Styles.black,
    fontFamily: Styles.FontFamily.figtreeRegular,
    fontSize: Styles.Fonts.f8,
  },
  qrCodeImage: {
    width: 53,
    height: 48,
  },
  messageLeft: {
    flex: 1,
    marginLeft: Styles.Spacing.m2 + Styles.Spacing.m1,
  },
  containerVertical: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  topRight: {
    left: 10,
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  topRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  arrow: {
    width: 24,
    height: 24,
    alignItems: 'center',
    alignSelf: 'center',
    justifyContent: 'center',
  },
  from: {
    color: Styles.darkColor,
    fontSize: Styles.Fonts.f7,
    fontWeight: 'bold',
    fontFamily: Styles.FontFamily.figtreeRegular,
  },
  commonRow: {
    flexDirection: 'row',
  },
  titleHeaderView: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flex: 1,
  },
  readView: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  pressableStyle: {
    marginTop: Styles.Spacing.m3,
    marginHorizontal: Styles.Spacing.m3,
    paddingBottom: Styles.Spacing.m3,
    flexDirection: 'row',
    minHeight: Styles.Heights.touchTargetHeight2,
  },
  mainView: {
    flex: 1,
    marginLeft: Styles.Spacing.m3,
  },
});

export default withGlobalize(SnackCanteenItem);
